import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';

type ScientistLevel = 'none' | 'apprentice' | 'scientist' | 'mad_scientist';

interface LabScientistContextType {
  clicks: number;
  level: ScientistLevel;
  levelUnlocked: ScientistLevel;
  showBadge: boolean;
  incrementClick: () => void;
  dismissBadge: () => void;
}

const THRESHOLDS = {
  apprentice: 50,
  scientist: 100,
  mad_scientist: 500,
};

const LEVEL_NAMES: Record<ScientistLevel, string> = {
  none: '',
  apprentice: 'Aprendiz de Laboratorio',
  scientist: 'Científico UGC Lab',
  mad_scientist: 'Científico Loco',
};

const LEVEL_EMOJIS: Record<ScientistLevel, string> = {
  none: '',
  apprentice: '🔬',
  scientist: '🧪',
  mad_scientist: '⚗️🔥',
};

export const LabScientistContext = createContext<LabScientistContextType | null>(null);

const STORAGE_KEY = 'ugclab_scientist_clicks';
const LEVEL_KEY = 'ugclab_scientist_level';

function getLevel(clicks: number): ScientistLevel {
  if (clicks >= THRESHOLDS.mad_scientist) return 'mad_scientist';
  if (clicks >= THRESHOLDS.scientist) return 'scientist';
  if (clicks >= THRESHOLDS.apprentice) return 'apprentice';
  return 'none';
}

export function LabScientistProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [clicks, setClicks] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored, 10) : 0;
  });
  
  const [levelUnlocked, setLevelUnlocked] = useState<ScientistLevel>(() => {
    if (typeof window === 'undefined') return 'none';
    const stored = localStorage.getItem(LEVEL_KEY);
    return (stored as ScientistLevel) || 'none';
  });
  
  const [showBadge, setShowBadge] = useState(false);
  const [lastCelebrated, setLastCelebrated] = useState<ScientistLevel>(levelUnlocked);

  const level = getLevel(clicks);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, clicks.toString());
  }, [clicks]);

  useEffect(() => {
    if (level !== 'none' && level !== lastCelebrated) {
      const levelOrder: ScientistLevel[] = ['none', 'apprentice', 'scientist', 'mad_scientist'];
      const currentIndex = levelOrder.indexOf(level);
      const lastIndex = levelOrder.indexOf(lastCelebrated);
      
      if (currentIndex > lastIndex) {
        setShowBadge(true);
        setLevelUnlocked(level);
        localStorage.setItem(LEVEL_KEY, level);
        setLastCelebrated(level);
        
        toast({
          title: `${LEVEL_EMOJIS[level]} ¡Nivel desbloqueado!`,
          description: `Has alcanzado el rango de ${LEVEL_NAMES[level]}`,
          duration: 5000,
        });
      }
    }
  }, [level, lastCelebrated, toast]);

  const incrementClick = useCallback(() => {
    setClicks(prev => prev + 1);
  }, []);

  const dismissBadge = useCallback(() => {
    setShowBadge(false);
  }, []);

  return (
    <LabScientistContext.Provider
      value={{
        clicks,
        level,
        levelUnlocked,
        showBadge,
        incrementClick,
        dismissBadge,
      }}
    >
      {children}
    </LabScientistContext.Provider>
  );
}

export function useLabScientist() {
  const context = useContext(LabScientistContext);
  if (!context) {
    throw new Error('useLabScientist must be used within a LabScientistProvider');
  }
  return context;
}

export { LEVEL_NAMES, LEVEL_EMOJIS, THRESHOLDS };
export type { ScientistLevel };
